import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  FormControl, InputLabel, MenuItem, Select,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PublishIcon from '@material-ui/icons/Publish';
import SaveIcon from '@material-ui/icons/Save';
import SearchIcon from '@material-ui/icons/Search';
import StorefrontOutlinedIcon from '@material-ui/icons/StorefrontOutlined';
import TuneIcon from '@material-ui/icons/Tune';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { searchProducts } from '../../../slices/ProductSelectorSlice';
import {
  changeMerchandisingQueryGroupStatus,
  clearPreview,
  clearQueryGroupEditorFeedback,
  deleteMerchandisingQueryGroup,
  fetchMerchandisingQueryGroup,
  fetchMerchandisingQueryGroups,
  fetchMerchandisingIndexState,
  runMerchandisingPreview,
  saveMerchandisingQueryGroup,
  setMerchandisingQueryLanguage,
} from '../state/merchandisingSlice';
import {
  emptyQueryWorkbench,
  previewFacetItem,
  previewProductCard,
  queryGroupToWorkbench,
  queryWorkbenchActions,
  queryWorkbenchPreviewRule,
  queryWorkbenchSharedRule,
} from '../model/queryRuleWorkbench';
import {
  formatLocalDateTime, InlineNotice, PageHeader, PriorityLabel, ProductImage, ScheduleCard,
  SearchInput, SegmentedControl, SelectionCheckbox, StateBoundary, StatusChip,
  WorkbenchPagination,
} from './MerchandisingWorkbenchUi';
import { FacetControl, ProductCard, PushPinIcon } from './SearchRuleCards';
import { CapabilityNotice } from './MerchandisingStatus';
import QueryGroupTransferControls from './QueryGroupTransferControls';
import IndexStatusBar from './IndexStatusBar';
import { UnsavedChangesDialog, useUnsavedChangesGuard } from './UnsavedChangesGuard';
import { advancedQuerySupport, queryExtensionError, queryExtensionEditable } from '../model/advancedQuerySupport';
import { QueryMatchControl, QueryResponseControl, QueryResponsePreview, QueryResponseChange, responseDraftValid } from './AdvancedQueryControls';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { useMerchandisingWorkbenchCopy } from './useMerchandisingWorkbenchCopy';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import { useRouteShopId } from '../../shell/context/useRouteShopId';
import {
  cloudLanguageScopes, normalizeLanguageScope, withLanguageScope,
} from '../model/languageScope';

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
  });
};

const groupScheduleState = (group) => {
  const now = Date.now();
  const from = group.valid_from ? Date.parse(group.valid_from) : null;
  const to = group.valid_to ? Date.parse(group.valid_to) : null;
  if (to && to < now) return 'expired';
  if (from && from > now) return 'scheduled';
  return from || to ? 'window' : 'always';
};

const validSchedule = (from, to) => {
  if (from && Number.isNaN(Date.parse(from))) return false;
  if (to && Number.isNaN(Date.parse(to))) return false;
  return !(from && to) || Date.parse(to) > Date.parse(from);
};

const QueryRuleLibrary = ({
  basePath, languageId, languageOptions, onLanguageChange, showLanguageSelector,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const feature = useSelector((state) => state.merchandising.queryGroups);
  const capability = useSelector((state) => state.merchandising.capability.queryGroups);
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState([]);
  const [bulkError, setBulkError] = useState(null);
  const [deleteCandidates, setDeleteCandidates] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (capability) dispatch(fetchMerchandisingQueryGroups({ languageId, limit: 100 }));
  }, [capability, dispatch, languageId]);

  useEffect(() => {
    const ruleId = new URLSearchParams(location.search).get('rule_id');
    if (!ruleId || !feature.order.length) return;
    const group = feature.order.map((id) => feature.entities[id]).find((candidate) =>
      (candidate?.members || []).some((member) => String(member.rule_id) === String(ruleId)));
    if (group) history.replace(withLanguageScope(
      `${basePath}/query/${group.id}`,
      normalizeLanguageScope(group.language_id, languageId),
    ));
  }, [basePath, feature.entities, feature.order, history, languageId, location.search]);

  const rows = feature.order.map((id) => feature.entities[id]).filter((group) => (
    group
    && group.status !== 'archived'
    && (status === 'all' || group.status === status)
    && `${group.name} ${(group.members || []).map((member) => member.query).join(' ')}`
      .toLowerCase().includes(filter.toLowerCase())
  ));
  const allVisible = rows.length > 0 && rows.every((row) => selected.includes(row.id));
  const viewState = feature.request.status === 'loading' && !feature.order.length
    ? 'loading'
    : (feature.request.status === 'error' ? 'error' : (rows.length ? 'ready' : 'empty'));
  const updateStatus = async (targetStatus) => {
    setBulkError(null);
    const targets = selected.map((id) => feature.entities[id]).filter((group) => {
      if (!group) return false;
      if (targetStatus === 'active') return ['draft', 'suggested', 'disabled'].includes(group.status);
      if (targetStatus === 'disabled') return group.status === 'draft' || group.status === 'active';
      return false;
    });
    const results = await Promise.all(targets.map(async (group) => ({
      group,
      result: await dispatch(changeMerchandisingQueryGroupStatus({
        id: group.id,
        revision: group.revision,
        status: targetStatus,
        languageId: normalizeLanguageScope(group.language_id, languageId),
      })),
    })));
    const failedIds = results
      .filter(({ result }) => !changeMerchandisingQueryGroupStatus.fulfilled.match(result))
      .map(({ group }) => group.id);
    const firstFailure = results.find(({ result }) =>
      !changeMerchandisingQueryGroupStatus.fulfilled.match(result));
    if (firstFailure) {
      setBulkError(firstFailure.result.payload?.message || firstFailure.result.error?.message || t('merchandising.workbench.loadError'));
    }
    if (failedIds.length > 0) {
      await Promise.all(failedIds.map((id) => dispatch(fetchMerchandisingQueryGroup({
        id,
        languageId: normalizeLanguageScope(feature.entities[id]?.language_id, languageId),
      }))));
    }
    setSelected(failedIds);
    if (failedIds.length === 0) {
      setSelected([]);
      dispatch(fetchMerchandisingQueryGroups({ languageId, limit: 100 }));
    }
  };
  const selectedGroups = selected.map((id) => feature.entities[id]).filter(Boolean);
  const canActivate = selectedGroups.some((group) => ['draft', 'suggested', 'disabled'].includes(group.status));
  const canDisable = selectedGroups.some((group) => ['draft', 'active'].includes(group.status));
  const deleteBusy = deleting && deleteCandidates.length > 0;
  const confirmDelete = async () => {
    if (deleteBusy) return;
    setBulkError(null);
    setDeleting(true);
    const results = await Promise.all(deleteCandidates.map(async (group) => ({
      group,
      result: await dispatch(deleteMerchandisingQueryGroup({
        id: group.id,
        revision: group.revision,
        languageId: normalizeLanguageScope(group.language_id, languageId),
      })),
    })));
    const deletedIds = results
      .filter(({ result }) => deleteMerchandisingQueryGroup.fulfilled.match(result))
      .map(({ group }) => group.id);
    const firstFailure = results.find(({ result }) =>
      !deleteMerchandisingQueryGroup.fulfilled.match(result));
    if (firstFailure) {
      setBulkError(firstFailure.result.payload?.message
        || firstFailure.result.error?.message
        || t('merchandising.workbench.loadError'));
    }
    setSelected((current) => current.filter((id) => !deletedIds.includes(id)));
    setDeleteCandidates((current) => current.filter((group) => !deletedIds.includes(group.id)));
    if (deletedIds.length > 0) {
      dispatch(fetchMerchandisingQueryGroups({ languageId, limit: 100 }));
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
    setDeleting(false);
  };

  return (
    <div>
      <PageHeader
        heading={C.SEARCH_RULES}
        body={C.SEARCH_RULES_DESCRIPTION}
        primaryLabel={C.CREATE_SEARCH_RULE}
        onPrimary={() => history.push(withLanguageScope(`${basePath}/query/new`, languageId))}
        secondary={<>
          <IndexStatusBar inline />
          <QueryGroupTransferControls
            groups={[]}
            languageId={languageId}
            onComplete={() => dispatch(fetchMerchandisingQueryGroups({ languageId, limit: 100 }))}
            actions={['import']}
          />
        </>}
      />
      {showLanguageSelector && (
        <div className="mp-language-scope-bar">
          <FormControl size="small" variant="outlined" className="mp-language-scope-control">
            <InputLabel id="mp-query-language-label">
              {t('merchandising.workbench.rulesLanguage')}
            </InputLabel>
            <Select
              labelId="mp-query-language-label"
              label={t('merchandising.workbench.rulesLanguage')}
              value={languageId}
              inputProps={{ 'aria-label': t('merchandising.workbench.rulesLanguage') }}
              onChange={(event) => onLanguageChange(event.target.value)}
            >
              {languageOptions.map((scope) => (
                <MenuItem key={scope} value={scope}>
                  {scope === 'default'
                    ? t('merchandising.workbench.defaultLanguage')
                    : scope}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      {!capability && <div className="mp-inline-error" role="alert">{t('merchandising.workbench.queryGroupsUnavailable')}</div>}
      {(bulkError || feature.mutation.error) && <div className="mp-inline-error" role="alert">{bulkError || feature.mutation.error.message}</div>}
      <StateBoundary
        state={viewState}
        onReady={() => dispatch(fetchMerchandisingQueryGroups({ languageId, limit: 100 }))}
        copy={C}
        emptyActionLabel={C.CREATE_SEARCH_RULE}
        onEmptyAction={() => history.push(withLanguageScope(`${basePath}/query/new`, languageId))}
      >
        <section className="mp-library-card" aria-label={C.SEARCH_RULES}>
          <div className="mp-library-toolbar">
            <SearchInput value={filter} onChange={setFilter} placeholder={C.SEARCH_RULES_PLACEHOLDER} />
            <label className="mp-select-field">
              <span className="sr-only">{C.STATUS}</span>
              <Select value={status} onChange={(event) => setStatus(event.target.value)} variant="outlined">
                <MenuItem value="all">{C.ALL_STATUSES}</MenuItem>
                <MenuItem value="active">{C.ACTIVE}</MenuItem>
                <MenuItem value="draft">{C.DRAFT}</MenuItem>
                <MenuItem value="disabled">{C.DISABLED}</MenuItem>
              </Select>
            </label>
            <QueryGroupTransferControls
              groups={selectedGroups}
              languageId={languageId}
              actions={['export']}
            />
          </div>
          <div className="mp-table-scroll"><div className="mp-rule-table" role="table" aria-label={C.SEARCH_RULES}>
            <div className="mp-rule-row mp-rule-row--head" role="row">
              <span role="columnheader"><SelectionCheckbox checked={allVisible} label={C.SELECT_ALL} onChange={() => setSelected(allVisible ? [] : rows.map((row) => row.id))} /></span>
              <span role="columnheader">{C.QUERY}</span>
              <span role="columnheader">{C.EFFECT}</span>
              <span role="columnheader">{C.STATUS}</span>
              <span role="columnheader">{C.UPDATED}</span>
              <span role="columnheader" />
            </div>
            {rows.map((group) => {
              const queries = (group.members || []).map((member) => member.query);
              return (
                <div key={group.id} className="mp-rule-row" role="row">
                  <span role="cell"><SelectionCheckbox checked={selected.includes(group.id)} label={`${C.SELECTED} ${group.name}`} onChange={() => setSelected((current) => current.includes(group.id) ? current.filter((id) => id !== group.id) : [...current, group.id])} /></span>
                  <span role="cell" className="mp-rule-primary"><Link to={withLanguageScope(`${basePath}/query/${group.id}`, normalizeLanguageScope(group.language_id, languageId))}>{queries[0] || group.name}</Link>{queries.length > 1 && <small>{t('merchandising.workbench.relatedSearches', { count: queries.length - 1 })}</small>}</span>
                  <span role="cell" className="mp-rule-primary">
                    <span>{queries.length > 1 ? t('merchandising.workbench.sharedSearches', { count: queries.length }) : t('merchandising.workbench.arrangedResults')}</span>
                    {groupScheduleState(group) !== 'always' && <small>{t(`merchandising.schedule.${groupScheduleState(group)}`)}</small>}
                  </span>
                  <span role="cell"><StatusChip status={group.status || 'draft'} copy={C} /></span>
                  <span role="cell" className="mp-muted">{formatLocalDateTime(group.updated_at, intl.locale) || '—'}</span>
                  <span role="cell" className="mp-row-actions">
                    <IconButton aria-label={`${C.DUPLICATE} ${group.name}`} onClick={() => history.push(withLanguageScope(`${basePath}/query/new`, normalizeLanguageScope(group.language_id, languageId), { from: group.id }))}><FileCopyOutlinedIcon fontSize="small" /></IconButton>
                    <IconButton aria-label={t('merchandising.workbench.deleteNamed', { name: group.name })} onClick={() => setDeleteCandidates([group])}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </span>
                </div>
              );
            })}
          </div></div>
          {feature.nextCursor && (
            <div className="mp-library-pagination">
              <Button
                variant="outlined"
                disabled={feature.request.status === 'loading'}
                onClick={() => dispatch(fetchMerchandisingQueryGroups({
                  languageId, cursor: feature.nextCursor, limit: 100,
                }))}
              >
                {t('merchandising.action.loadMore')}
              </Button>
            </div>
          )}
        </section>
        {selected.length > 0 && (
          <div className="mp-bulk-bar" role="region" aria-label={`${selected.length} ${C.SELECTED}`}>
            <strong>{selected.length} {C.SELECTED}</strong>
            <QueryGroupTransferControls
              groups={selectedGroups}
              languageId={languageId}
              onComplete={() => dispatch(fetchMerchandisingQueryGroups({ languageId, limit: 100 }))}
              actions={['duplicate']}
            />
            {canActivate && <Button onClick={() => updateStatus('active')}>{t('merchandising.action.activate')}</Button>}
            {canDisable && <Button onClick={() => updateStatus('disabled')}>{C.DISABLE}</Button>}
            <Button className="mp-destructive-action" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteCandidates(selectedGroups)}>{t('merchandising.action.delete')}</Button>
          </div>
        )}
      </StateBoundary>
      <Dialog className="mp-dialog" open={deleteCandidates.length > 0} onClose={() => { if (!deleteBusy) setDeleteCandidates([]); }} maxWidth="xs" fullWidth>
        <DialogTitle>{t('merchandising.workbench.deleteTitle')}</DialogTitle>
        <DialogContent>
          <p>{t('merchandising.workbench.deleteWarning', { count: deleteCandidates.length })}</p>
          {bulkError && <div className="mp-inline-error" role="alert">{bulkError}</div>}
        </DialogContent>
        <DialogActions className="mp-dialog-actions"><Button variant="outlined" disabled={deleteBusy} onClick={() => setDeleteCandidates([])}>{C.CANCEL}</Button><Button className="mp-danger-button" variant="contained" disabled={deleteBusy} startIcon={deleteBusy ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />} onClick={confirmDelete}>{deleteBusy ? t('merchandising.action.deleting') : t('merchandising.action.delete')}</Button></DialogActions>
      </Dialog>
    </div>
  );
};

QueryRuleLibrary.propTypes = {
  basePath: PropTypes.string.isRequired,
  languageId: PropTypes.string.isRequired,
  languageOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  showLanguageSelector: PropTypes.bool.isRequired,
};

export const previewBoard = (preview) => {
  const current = (preview?.current?.products || []).map(previewProductCard);
  const proposed = (preview?.proposed?.products || []).map(previewProductCard);
  const byId = Object.fromEntries([...current, ...proposed].map((product) => [product.ID, product]));
  const proposedOrder = proposed.map((product) => product.ID);
  const ordered = [...proposedOrder];
  current.forEach((product, naturalIndex) => {
    if (ordered.includes(product.ID)) return;
    ordered.splice(Math.min(naturalIndex, ordered.length), 0, product.ID);
  });
  return ordered.map((id) => byId[id]).filter(Boolean);
};

export const facetGroupsFromSearchFacets = (searchFacets = []) => {
  const groups = { categories: [], brands: [] };
  (Array.isArray(searchFacets) ? searchFacets : []).forEach((facet) => {
    const key = String(facet?.key || '').toLowerCase();
    let bucket = null;
    if (['category', 'categories', 'category_id', 'category_ids'].includes(key)) {
      bucket = 'categories';
    } else if (['brand', 'brands', 'brand_id', 'manufacturer', 'vendor'].includes(key)) {
      bucket = 'brands';
    }
    if (!bucket) return;
    groups[bucket] = (facet.values || []).map((value) => {
      const rawIdentity = String(value.value ?? value.id);
      // The legacy search facet is encoded as "stable-id|display path".
      // Merchandising must persist only the stable ID: Elasticsearch indexes
      // category memberships by that ID, never by the presentation label.
      const separator = bucket === 'categories' ? rawIdentity.indexOf('|') : -1;
      const id = separator > 0 ? rawIdentity.slice(0, separator) : rawIdentity;
      const encodedPath = separator > 0 ? rawIdentity.slice(separator + 1).trim() : '';
      return {
        id,
        name: value.label || value.name || encodedPath || id,
        count: Number(value.count) || 0,
        path: value.path || encodedPath || null,
      };
    });
  });
  return groups;
};

export const mergePreviewFacetGroups = (...sources) => {
  const merged = { categories: [], brands: [] };
  ['categories', 'brands'].forEach((bucket) => {
    const byId = new Map();
    sources.forEach((source) => (source?.[bucket] || []).forEach((item) => {
      const id = String(item.id);
      byId.set(id, { ...(byId.get(id) || {}), ...item, id });
    }));
    merged[bucket] = [...byId.values()];
  });
  return merged;
};

export const productGroupState = (product, groupChanges) => {
  let factor = 1;
  let excluded = false;
  const memberships = product.MEMBERSHIPS || {};
  const apply = (changes, field) => Object.entries(changes || {}).forEach(([id, effect]) => {
    if (!(memberships[field] || []).map(String).includes(String(id)) || !effect) return;
    if (effect.TYPE === 'exclude') excluded = true;
    else factor *= Number(effect.FACTOR || 1);
  });
  apply(groupChanges.categories, 'category_ids');
  apply(groupChanges.brands, 'brand_id');

  return { excluded, factor: Math.max(0.05, Math.min(5, factor)) };
};

export const productGroupExclusionApplies = (groupState, change) => (
  Boolean(groupState?.excluded)
  && !['hidden', 'pinned', 'added'].includes(change?.TYPE)
);

const pickerProductCard = (product = {}) => previewProductCard({
  id: product.id,
  name: product.name,
  image_url: product.image_url || product.thumbnail_url || product.thumbnail || product.image,
  reference: product.reference || product.vendorCode,
  vendor: product.vendor,
  category_name: product.category_name || product.categoryName,
  price: product.price,
});

export const buildQueryChangeRows = ({ model, products, addedProducts, facets }) => {
  const responsePolicy = model.responsePolicy;
  if (responsePolicy?.mode === 'empty') model = { ...model, productChanges: {}, groupChanges: { categories: {}, brands: {} } };
  const productById = Object.fromEntries(products.map((product) => [String(product.ID), product]));
  Object.entries(addedProducts || {}).forEach(([id, product]) => {
    productById[String(id)] = pickerProductCard(product);
  });
  const groupById = {
    categories: Object.fromEntries((facets.categories || []).map((item) => [String(item.id), previewFacetItem(item)])),
    brands: Object.fromEntries((facets.brands || []).map((item) => [String(item.id), previewFacetItem(item)])),
  };
  const rows = responsePolicy ? [{ id: 'response', type: 'response', response: responsePolicy }] : [];
  if (!responsePolicy && model.rewrite?.trim()) rows.push({ id: 'rewrite', type: 'rewrite', query: model.rewrite.trim() });
  Object.entries(model.productChanges || {}).forEach(([id, change]) => {
    if (['pinned', 'added'].includes(change?.TYPE)) {
      rows.push({ id: `product-pin-${id}`, type: 'pin', product: productById[id] || previewProductCard({ id }), position: Number(change.POSITION) });
    } else if (change?.TYPE === 'hidden') {
      rows.push({ id: `product-hide-${id}`, type: 'hide', product: productById[id] || previewProductCard({ id }) });
    }
  });
  ['categories', 'brands'].forEach((bucket) => {
    Object.entries(model.groupChanges[bucket] || {}).forEach(([id, effect]) => {
      if (!effect) return;
      rows.push({
        id: `${bucket}-${id}`,
        type: effect.TYPE === 'exclude' ? 'group-exclude' : 'group-priority',
        bucket,
        group: groupById[bucket][id] || previewFacetItem({ id }),
        factor: Number(effect.FACTOR || 1),
      });
    });
  });
  if (model.validFrom || model.validTo) {
    rows.push({ id: 'schedule', type: 'schedule', validFrom: model.validFrom, validTo: model.validTo });
  }
  return rows;
};

const QueryChangeReview = ({ rows, copy, locale }) => (
  <div className="mp-preview-change-list">
    {rows.length ? (
      <div className="mp-change-review">
        {rows.map((change) => {
          if (change.type === 'response') return <QueryResponseChange key={change.id} response={change.response} />;
          if (change.type === 'pin' || change.type === 'hide') {
            return (
              <article key={change.id} className={`mp-change-review-card is-${change.type}`}>
                <ProductImage product={change.product} size="thumb" />
                <div><strong>{change.product.PRODUCT_NAME}</strong><span>{change.product.SKU}</span></div>
                <span className="mp-change-effect">
                  {change.type === 'pin' ? <PushPinIcon /> : <CloseIcon />}
                  {change.type === 'pin' ? `#${change.position}` : copy.HIDDEN}
                </span>
              </article>
            );
          }
          if (change.type === 'group-priority' || change.type === 'group-exclude') {
            const GroupIcon = change.bucket === 'categories' ? CategoryOutlinedIcon : StorefrontOutlinedIcon;
            return (
              <article key={change.id} className={`mp-change-review-card is-group ${change.type === 'group-exclude' ? 'is-excluded' : ''}`}>
                <span className="mp-change-symbol"><GroupIcon /></span>
                <div><strong>{change.group.NAME}</strong><span>{change.bucket === 'categories' ? copy.CATEGORIES : copy.BRANDS}</span></div>
                {change.type === 'group-exclude'
                  ? <span className="mp-change-effect is-excluded"><CloseIcon />{copy.EXCLUDED}</span>
                  : <PriorityLabel factor={change.factor} copy={copy} />}
              </article>
            );
          }
          if (change.type === 'schedule') {
            return (
              <article key={change.id} className="mp-change-review-card is-setting">
                <span className="mp-change-symbol"><CalendarTodayOutlinedIcon /></span>
                <div><strong>{copy.SCHEDULE}</strong><span>{[formatLocalDateTime(change.validFrom, locale), formatLocalDateTime(change.validTo, locale)].filter(Boolean).join(' — ')}</span></div>
              </article>
            );
          }
          return (
            <article key={change.id} className="mp-change-review-card is-setting">
              <span className="mp-change-symbol"><SearchIcon /></span>
              <div><strong>{copy.SEARCH_INSTEAD}</strong><span>{change.query}</span></div>
            </article>
          );
        })}
      </div>
    ) : <p>{copy.NO_PREVIEW_CHANGES}</p>}
  </div>
);

QueryChangeReview.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  copy: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
};

export const optimisticPreviewBoard = (
  preview, productChanges = {}, addedProducts = {}, page = 1, pageSize = 20,
  { groupChanges = { categories: {}, brands: {} }, hideExcluded = false } = {},
) => {
  const board = previewBoard(preview);
  const byId = Object.fromEntries(board.map((product) => [product.ID, product]));
  Object.entries(addedProducts).forEach(([id, product]) => {
    byId[String(id)] = pickerProductCard(product);
  });
  const pins = Object.entries(productChanges)
    .filter(([, change]) => ['pinned', 'added'].includes(change?.TYPE))
    .map(([id, change]) => ({ id: String(id), position: Number(change.POSITION) }))
    .filter(({ position }) => Number.isFinite(position))
    .sort((left, right) => left.position - right.position);
  const pinnedIds = new Set(pins.map(({ id }) => id));
  const ordered = board.filter((product) => !pinnedIds.has(product.ID));
  const pageStart = ((page - 1) * pageSize) + 1;
  pins.forEach(({ id, position }) => {
    const product = byId[id];
    if (!product || position < pageStart || position >= pageStart + pageSize) return;
    ordered.splice(Math.max(0, Math.min(position - pageStart, ordered.length)), 0, product);
  });

  const visible = hideExcluded ? ordered.filter((product) => {
    const change = productChanges[product.ID];
    if (change?.TYPE === 'hidden') return false;
    return !productGroupExclusionApplies(productGroupState(product, groupChanges), change);
  }) : ordered;
  return visible.slice(0, pageSize);
};

export const executablePreviewGroupChanges = (
  groupChanges,
  membershipProjectionPending,
  factsProjectionPending = membershipProjectionPending,
) => ({
  categories: membershipProjectionPending ? {} : groupChanges.categories,
  brands: factsProjectionPending ? {} : groupChanges.brands,
});

const QueryRuleEditor = ({ basePath, groupId, languageId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const t = useLocalizedMessage();
  const C = useMerchandisingWorkbenchCopy();
  const current = useSelector((state) => state.merchandising.queryGroups.current);
  const groupRequest = useSelector((state) => state.merchandising.queryGroups.request);
  const mutation = useSelector((state) => state.merchandising.queryGroups.mutation);
  const previewState = useSelector((state) => state.merchandising.previews.query);
  const schema = useSelector((state) => state.merchandising.discovery.schema);
  const capability = useSelector((state) => state.merchandising.capability);
  const scopeGeneration = useSelector((state) => state.merchandising.scopeGeneration);
  const support = useMemo(() => advancedQuerySupport({ schema, runtime: capability.runtime, bootstrap: capability.bootstrap }), [schema, capability]);
  const [model, setModel] = useState(() => emptyQueryWorkbench('', languageId));
  const [baseline, setBaseline] = useState(() => emptyQueryWorkbench('', languageId));
  const [activeQuery, setActiveQuery] = useState('');
  const [addingQuery, setAddingQuery] = useState(false);
  const [queryDraft, setQueryDraft] = useState('');
  const [expandedFacet, setExpandedFacet] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [rewriteEnabled, setRewriteEnabled] = useState(false);
  const [resultView, setResultView] = useState('preview');
  const [hideExcluded, setHideExcluded] = useState(false);
  const [previewQueued, setPreviewQueued] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productFilter, setProductFilter] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchBusy, setProductSearchBusy] = useState(false);
  const [addedProducts, setAddedProducts] = useState({});
  const [draggedProduct, setDraggedProduct] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [resultPage, setResultPage] = useState(1);
  const [localError, setLocalError] = useState(null);
  const [responseResetVersion, setResponseResetVersion] = useState(0);
  const loadedGroupId = useRef(null);
  const productSearchGeneration = useRef(0);
  const previewGeneration = useRef(0);
  const savedNavigationId = useRef(null);
  const autoExpandedFacetQuery = useRef(null);
  const queryInput = useRef(null);
  const locationParams = new URLSearchParams(location.search);
  const duplicateId = locationParams.get('from');
  const prefilledQuery = (locationParams.get('query') || '').trim().slice(0, 512);
  const candidateReturnTo = locationParams.get('return_to');
  const analyticsPathPrefix = `${basePath.split('/search/')[0]}/search/analytic/`;
  const returnTo = candidateReturnTo
    && candidateReturnTo.length <= 1024
    && candidateReturnTo.startsWith(analyticsPathPrefix)
    ? candidateReturnTo
    : null;
  const sourceId = groupId || duplicateId;

  useEffect(() => {
    const preservedSavedEditor = sourceId && savedNavigationId.current === sourceId;
    savedNavigationId.current = null;
    setLocalError(null);
    setResultPage(1);
    setProductDialogOpen(false);
    setProductFilter('');
    setProductOptions([]);
    setAddedProducts({});
    setExpandedFacet(null);
    autoExpandedFacetQuery.current = null;
    setScheduleEnabled(false);
    setRewriteEnabled(false);
    setResultView('preview');
    setHideExcluded(false);
    setPreviewQueued(false);
    dispatch(clearQueryGroupEditorFeedback());
    loadedGroupId.current = preservedSavedEditor ? sourceId : null;
    if (preservedSavedEditor) return;
    dispatch(clearPreview('query'));
    if (!sourceId) {
      const empty = emptyQueryWorkbench(prefilledQuery, languageId);
      setModel(empty);
      setBaseline(empty);
      setActiveQuery(prefilledQuery);
      return;
    }
    dispatch(fetchMerchandisingQueryGroup({ id: sourceId, languageId }));
  }, [dispatch, languageId, prefilledQuery, sourceId, scopeGeneration]);

  useEffect(() => {
    if (!sourceId) return;
    if (current?.group?.id !== sourceId || loadedGroupId.current === sourceId) return;
    const next = queryGroupToWorkbench(current, languageId);
    if (!groupId) {
      next.id = null;
      next.revision = null;
      next.status = 'draft';
      next.name = '';
    }
    setModel(next);
    setBaseline(next);
    setActiveQuery(next.queries[0] || '');
    setScheduleEnabled(Boolean(next.validFrom || next.validTo));
    setRewriteEnabled(Boolean(next.rewrite));
    loadedGroupId.current = sourceId;
  }, [current, groupId, languageId, sourceId]);

  const actions = useMemo(() => queryWorkbenchActions(model), [model]);
  const extensionError = model.extensionMismatch || queryExtensionError({ rule: queryWorkbenchSharedRule(model), matchConfig: model.matchConfig, schema, runtime: capability.runtime, bootstrap: capability.bootstrap });
  const extensionEditable = queryExtensionEditable(model, schema);
  const responseValid = !extensionError && responseDraftValid(model.responsePolicy);
  const hasChanges = Boolean(actions.length || model.responsePolicy);
  const readOnly = model.status === 'archived' || Boolean(extensionError);
  const previewFingerprint = JSON.stringify({
    activeQuery, actions, responsePolicy: model.responsePolicy, matchConfig: model.matchConfig, responseValid, extensionError, languageId: model.languageId, page: resultPage,
  });
  useEffect(() => {
    const query = activeQuery.trim();
    const ownGeneration = ++previewGeneration.current;
    if (!query || !responseValid || extensionError) {
      setPreviewQueued(false);
      return undefined;
    }
    setPreviewQueued(true);
    const timer = window.setTimeout(async () => {
      await dispatch(runMerchandisingPreview({
        workspace: 'query',
        payload: {
          query,
          language_id: model.languageId,
          ...(hasChanges ? { draft_rule: queryWorkbenchPreviewRule(model, query) } : {}),
          filters: {},
          ranges: [],
          sort: 'relevance',
          page: resultPage,
          page_size: 20,
        },
      }));
      if (previewGeneration.current === ownGeneration) setPreviewQueued(false);
    }, 600);
    return () => {
      window.clearTimeout(timer);
      if (previewGeneration.current === ownGeneration) previewGeneration.current += 1;
    };
  // The stable serialized fingerprint intentionally owns preview scheduling.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, previewFingerprint]);

  useEffect(() => {
    if (!productDialogOpen) return undefined;
    if (!productFilter.trim()) {
      setProductOptions([]);
      setProductSearchBusy(false);
      return undefined;
    }
    const ownGeneration = ++productSearchGeneration.current;
    const timer = window.setTimeout(async () => {
      setProductSearchBusy(true);
      try {
        const result = await dispatch(searchProducts({ query: productFilter.trim(), limit: 12 }));
        if (ownGeneration !== productSearchGeneration.current) return;
        if (searchProducts.fulfilled.match(result)) {
          const payload = result.payload;
          setProductOptions(Array.isArray(payload) ? payload : (payload?.items || []));
        }
        else setLocalError(result.payload || t('product.selector.search.error'));
      } catch (error) {
        setLocalError(error.message || t('product.selector.search.error'));
      } finally {
        if (ownGeneration === productSearchGeneration.current) setProductSearchBusy(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [dispatch, productDialogOpen, productFilter, t]);

  const preview = previewState.result?.preview;
  const emptyResponsePreview = resultView === 'preview' && model.responsePolicy?.mode === 'empty';
  const membershipPreviewPending = previewState.result?.preview_status
    ?.membership_projection_pending === true;
  const factsPreviewPending = previewState.result?.preview_status
    ?.facts_projection_pending === true;
  const effectivePreviewGroupChanges = executablePreviewGroupChanges(
    model.groupChanges,
    membershipPreviewPending,
    factsPreviewPending,
  );
  const previewProducts = model.responsePolicy ? (preview?.proposed?.products || []).map(previewProductCard) : optimisticPreviewBoard(
    preview, model.productChanges, addedProducts, resultPage, 20,
    { groupChanges: effectivePreviewGroupChanges, hideExcluded },
  );
  const originalResult = previewState.result?.natural?.results || {};
  const originalProducts = (originalResult.products || []).map(previewProductCard);
  const products = resultView === 'original' ? originalProducts : previewProducts;
  const facets = mergePreviewFacetGroups(
    facetGroupsFromSearchFacets(previewState.result?.natural?.facets),
    preview?.current?.groups,
    preview?.proposed?.groups,
  );
  useEffect(() => {
    const query = activeQuery.trim();
    const firstCategory = (facets.categories || []).map(previewFacetItem)[0];
    if (!query || !firstCategory || autoExpandedFacetQuery.current === query) return;
    autoExpandedFacetQuery.current = query;
    setExpandedFacet(`categories:${firstCategory.ID}`);
  }, [activeQuery, facets.categories]);
  const previewBusy = !emptyResponsePreview && Boolean(activeQuery.trim()) && (
    previewQueued || previewState.request.refreshing || previewState.request.status === 'loading'
  );
  const hasPreviewResults = Boolean(activeQuery.trim() && (previewState.result || emptyResponsePreview));
  const hasExclusionActions = Object.values(model.productChanges || {})
    .some(change => change?.TYPE === 'hidden')
    || ['categories', 'brands'].some(bucket => (
      Object.values(effectivePreviewGroupChanges[bucket] || {})
        .some(effect => effect?.TYPE === 'exclude')
    ));
  const previewTotal = resultView === 'original'
    ? Number(originalResult.total || 0)
    : model.responsePolicy?.mode === 'empty' ? 0 : Number(preview?.proposed?.total || 0);
  const changeRows = useMemo(() => buildQueryChangeRows({
    model,
    products: [...previewProducts, ...originalProducts],
    addedProducts,
    facets,
  }), [addedProducts, facets, model, originalProducts, previewProducts]);
  const dirty = JSON.stringify(model) !== JSON.stringify(baseline);
  const unsavedGuard = useUnsavedChangesGuard(dirty);
  const queryReady = Boolean(activeQuery.trim());
  const scheduleIsValid = validSchedule(model.validFrom, model.validTo);
  const productChange = (id, value) => setModel((valueModel) => ({
    ...valueModel,
    productChanges: (() => {
      const next = { ...valueModel.productChanges };
      if (value) next[id] = value;
      else delete next[id];
      return next;
    })(),
  }));
  const setFacet = (bucket, id, value) => setModel((valueModel) => ({
    ...valueModel,
    groupChanges: {
      ...valueModel.groupChanges,
      [bucket]: (() => {
        const next = { ...valueModel.groupChanges[bucket] };
        if (value) next[id] = value;
        else delete next[id];
        return next;
      })(),
    },
  }));
  const normalizePins = (order, movedId) => {
    setModel((valueModel) => {
      const next = { ...valueModel.productChanges };
      const pinIds = new Set(Object.entries(next)
        .filter(([, change]) => ['pinned', 'added'].includes(change.TYPE))
        .map(([id]) => id));
      pinIds.add(movedId);
      let position = (resultPage - 1) * 20;
      order.forEach((id) => {
        if (next[id]?.TYPE === 'hidden') return;
        position += 1;
        if (pinIds.has(id)) {
          next[id] = {
            TYPE: next[id]?.TYPE === 'added' ? 'added' : 'pinned',
            POSITION: position,
            ...(next[id]?.ADMISSION ? { ADMISSION: next[id].ADMISSION } : {}),
          };
        }
      });
      return { ...valueModel, productChanges: next };
    });
  };
  const pinLast = (id, added = false, product = null) => {
    const last = Object.values(model.productChanges)
      .filter((change) => ['pinned', 'added'].includes(change.TYPE))
      .reduce((maximum, change) => Math.max(maximum, Number(change.POSITION) || 0), 0);
    productChange(id, { TYPE: added ? 'added' : 'pinned', POSITION: last + 1 });
    if (added && product) {
      setAddedProducts((currentProducts) => ({ ...currentProducts, [String(id)]: product }));
    }
    setResultPage(1);
  };
  const dropAt = (event, targetId) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData('text/plain');
    const order = previewProducts.map((product) => product.ID);
    const source = order.indexOf(sourceId);
    const target = order.indexOf(targetId);
    if (source >= 0 && target >= 0 && source !== target) {
      order.splice(source, 1);
      order.splice(target, 0, sourceId);
      normalizePins(order, sourceId);
    }
    setDraggedProduct(null);
    setDropTarget(null);
  };
  const moveProduct = (id, direction) => {
    const order = previewProducts.map((product) => product.ID);
    const source = order.indexOf(id);
    if (source < 0) return;
    let target = source + direction;
    while (target >= 0 && target < order.length) {
      const targetId = order[target];
      const targetChange = model.productChanges[targetId];
      const targetProduct = previewProducts.find((product) => product.ID === targetId);
      const targetBlocked = targetChange?.TYPE === 'hidden'
        || (targetProduct && productGroupExclusionApplies(
          productGroupState(targetProduct, model.groupChanges),
          targetChange,
        ));
      if (!targetBlocked) break;
      target += direction;
    }
    if (target < 0 || target >= order.length || target === source) return;
    order.splice(source, 1);
    order.splice(target, 0, id);
    normalizePins(order, id);
  };
  const save = useCallback(async (publish) => {
    const queries = model.queries.map((query) => query.trim()).filter(Boolean);
    if (!queries.length) {
      setLocalError(t('merchandising.workbench.queryRequired'));
      return;
    }
    if (extensionError || !responseValid) {
      setLocalError(t(extensionError ? 'merchandising.advanced.unsupported' : 'merchandising.advanced.invalidResponse'));
      return;
    }
    if (!hasChanges) {
      setLocalError(t('merchandising.workbench.changeRequired'));
      return;
    }
    if (!scheduleIsValid) {
      setLocalError(t('merchandising.workbench.invalidSchedule'));
      return;
    }
    setLocalError(null);
    const result = await dispatch(saveMerchandisingQueryGroup({
      ...(model.id ? { id: model.id, revision: model.revision } : { clientMutationId: uuid() }),
      languageId: model.languageId,
      queries,
      ...(model.matchConfig ? { matchConfig: model.matchConfig } : {}),
      rule: queryWorkbenchSharedRule(model),
      name: model.name.trim() || null,
      note: model.note.trim() || null,
    }));
    if (!saveMerchandisingQueryGroup.fulfilled.match(result)) return;
    let saved = result.payload;
    if (publish && saved.group.status !== 'active') {
      const activated = await dispatch(changeMerchandisingQueryGroupStatus({
        id: saved.group.id,
        revision: saved.group.revision,
        status: 'active',
        languageId: normalizeLanguageScope(saved.group.language_id, model.languageId),
      }));
      if (!changeMerchandisingQueryGroupStatus.fulfilled.match(activated)) return;
      saved = activated.payload;
    }
    if (saved.runtime_effect === 'reindex_required') {
      await dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }
    const savedLanguageId = normalizeLanguageScope(saved.group.language_id, model.languageId);
    const refreshed = await dispatch(fetchMerchandisingQueryGroup({
      id: saved.group.id,
      languageId: savedLanguageId,
    }));
    const canonicalSource = fetchMerchandisingQueryGroup.fulfilled.match(refreshed)
      ? refreshed.payload
      : saved;
    const canonical = queryGroupToWorkbench(canonicalSource, savedLanguageId);
    if (!canonical.queries.some((query) => query.trim()) && queries.length) canonical.queries = queries;
    setModel(canonical);
    setBaseline(canonical);
    setActiveQuery(canonical.queries[0] || queries[0]);
    setRewriteEnabled(Boolean(canonical.rewrite));
    loadedGroupId.current = saved.group.id;
    savedNavigationId.current = saved.group.id;
    const savedParams = new URLSearchParams();
    savedParams.set('language_id', canonical.languageId);
    if (returnTo) savedParams.set('return_to', returnTo);
    const savedSuffix = savedParams.toString();
    unsavedGuard.navigateWithoutPrompt(() => history.replace(
      `${basePath}/query/${saved.group.id}${savedSuffix ? `?${savedSuffix}` : ''}`,
    ));
  }, [hasChanges, extensionError, responseValid, basePath, dispatch, history, model, returnTo, scheduleIsValid, t, unsavedGuard]);

  const active = model.status === 'active';
  const existingRule = Boolean(model.id);
  const primarySavesOnly = active || existingRule;
  const pageCount = Math.max(1, Math.ceil(previewTotal / 20));
  useEffect(() => {
    if (resultPage > pageCount) setResultPage(pageCount);
  }, [pageCount, resultPage]);
  const editorRouteReady = sourceId
    ? loadedGroupId.current === sourceId
    : model.id === null;

  if (!editorRouteReady || (sourceId && groupRequest.status === 'loading' && current?.group?.id !== sourceId)) {
    return <StateBoundary state="loading" onReady={() => {}} copy={C}><span /></StateBoundary>;
  }

  const scheduleCard = (
    <ScheduleCard
      enabled={scheduleEnabled}
      validFrom={model.validFrom}
      validTo={model.validTo}
      disabled={readOnly}
      copy={C}
      onEnabledChange={(enabled) => {
        setScheduleEnabled(enabled);
        if (!enabled) setModel((currentModel) => ({ ...currentModel, validFrom: null, validTo: null }));
      }}
      onChange={(field, value) => setModel((currentModel) => ({ ...currentModel, [field]: value }))}
    />
  );
  return (
    <div>
      <PageHeader
        eyebrow={(
          <Link className="mp-back-link" to={returnTo || withLanguageScope(`${basePath}/query`, model.languageId)}>
            <ArrowBackIcon />
            {returnTo ? t('merchandising.editor.returnToAnalytics') : C.BACK_TO_RULES}
          </Link>
        )}
        heading={model.queries.length > 1
          ? `${C.ARRANGE_RESULTS_FOR} ${t('merchandising.workbench.searchCount', { count: model.queries.length })}`
          : (activeQuery ? `${C.ARRANGE_RESULTS_FOR} “${activeQuery}”` : C.CREATE_SEARCH_RULE)}
        body={null}
        primaryLabel={primarySavesOnly ? t('merchandising.action.saveChanges') : C.PUBLISH}
        primaryIcon={primarySavesOnly ? <SaveIcon /> : <PublishIcon />}
        primaryDisabled={readOnly || !hasChanges || !responseValid || !scheduleIsValid || (primarySavesOnly && !dirty)}
        onPrimary={() => save(!primarySavesOnly)}
        secondary={(
          <>
            {dirty && <Button variant="outlined" onClick={() => { setModel(baseline); setResponseResetVersion(version => version + 1); setActiveQuery(baseline.queries[0] || ''); setScheduleEnabled(Boolean(baseline.validFrom || baseline.validTo)); setRewriteEnabled(Boolean(baseline.rewrite)); }}>{C.RESET_CHANGES}</Button>}
            {!active && existingRule && <Button variant="outlined" disabled={readOnly || !hasChanges || !responseValid || !scheduleIsValid} onClick={() => save(true)}>{C.PUBLISH}</Button>}
            {!active && !existingRule && dirty && <Button variant="outlined" disabled={readOnly || !scheduleIsValid} onClick={() => save(false)}>{t('merchandising.action.saveDraft')}</Button>}
            <IndexStatusBar inline />
          </>
        )}
      />
      {readOnly && <div className="mp-inline-error mp-inline-error--editor" role="status">{t(extensionError ? 'merchandising.advanced.unsupported' : 'merchandising.workbench.archivedQuery')}</div>}
      {(!scheduleIsValid || localError || mutation.error || previewState.request.error) && (
        <InlineNotice
          dismissLabel={C.CLOSE}
          onDismiss={() => {
            setLocalError(null);
            dispatch(clearQueryGroupEditorFeedback());
            if (previewState.request.error) dispatch(clearPreview('query'));
          }}
        >
          {!scheduleIsValid
            ? t('merchandising.workbench.invalidSchedule')
            : (localError || mutation.error?.message || previewState.request.error?.message)}
        </InlineNotice>
      )}
      <fieldset className="mp-query-editor-fields" disabled={readOnly}>
      <section className="mp-query-bar">
        <label className="mp-active-query-field"><span>{C.PREVIEWING_SEARCH}</span><input ref={queryInput} autoFocus={!sourceId} placeholder={t('merchandising.workbench.queryPlaceholder')} value={activeQuery} onChange={(event) => {
          const value = event.target.value;
          setModel((currentModel) => ({ ...currentModel, queries: currentModel.queries.map((query) => query === activeQuery ? value : query) }));
          setActiveQuery(value);
        }} /></label>
        <QueryMatchControl key={scopeGeneration} model={model} persistedMatchConfig={baseline.matchConfig} support={support} disabled={readOnly} onChange={matchConfig => setModel(currentModel => ({ ...currentModel, matchConfig }))} />
        {queryReady && <Button className="mp-mobile-inspector-trigger" variant="outlined" onClick={() => setInspectorOpen(true)} startIcon={<TuneIcon />}>{C.RULE_SETTINGS} · {actions.length}</Button>}
        {model.queries.some((query) => query.trim()) && <div className="mp-query-variants">
          <span>{support.cloudEnabled ? t('merchandising.matching.phrasesTitle') : C.SAME_CHANGES_APPLY}</span>
          <div>{model.queries.filter((query) => query.trim()).map((query) => (
            <span key={query} className={`mp-query-chip ${query === activeQuery ? 'is-active' : ''}`}>
              <button type="button" aria-pressed={query === activeQuery} onClick={() => setActiveQuery(query)}>{query || t('merchandising.workbench.untitledSearch')}</button>
              {model.queries.length > 1 && <button type="button" aria-label={t('merchandising.workbench.removeQuery', { query })} onClick={() => {
                const next = model.queries.filter((candidate) => candidate !== query);
                setModel({ ...model, queries: next });
                if (activeQuery === query) setActiveQuery(next[0]);
              }}>{t('merchandising.workbench.multiplierSymbol')}</button>}
            </span>
          ))}<Button className="mp-add-query-variant" startIcon={<AddIcon />} onClick={() => setAddingQuery(true)}>{support.cloudEnabled ? t('merchandising.matching.addPhrase') : C.ADD_RELATED_SEARCH}</Button></div>
        </div>}
        {addingQuery && (
          <form className="mp-add-query" onSubmit={(event) => {
            event.preventDefault();
            const query = queryDraft.trim();
            if (!query || model.queries.some((candidate) => candidate.toLowerCase() === query.toLowerCase())) return;
            setModel({ ...model, queries: [...model.queries.filter(Boolean), query] });
            setActiveQuery(query);
            setQueryDraft('');
            setAddingQuery(false);
          }}>
            <label><span>{C.RELATED_SEARCH}</span><input autoFocus value={queryDraft} onChange={(event) => setQueryDraft(event.target.value)} /></label>
            <Button type="submit" variant="contained" color="primary">{C.ADD_SEARCH}</Button>
            <Button onClick={() => setAddingQuery(false)}>{C.CANCEL}</Button>
          </form>
        )}
      </section>
      <div className={`mp-workbench-layout ${queryReady ? '' : 'is-query-empty'}`}>
        <main className="mp-preview-canvas">
          {!queryReady ? (
            <section className="mp-query-start-state" aria-labelledby="mp-query-start-title">
              <span className="mp-query-start-icon" aria-hidden="true"><SearchIcon /></span>
              <h2 id="mp-query-start-title">{t('merchandising.workbench.queryStartTitle')}</h2>
              <p>{t('merchandising.workbench.queryStartBody')}</p>
            </section>
          ) : <>{(membershipPreviewPending || factsPreviewPending) && (
            <InlineNotice tone="neutral">
              {t(membershipPreviewPending
                ? 'merchandising.workbench.membershipPreviewPending'
                : 'merchandising.workbench.factsPreviewPending')}
            </InlineNotice>
          )}<div className="mp-preview-toolbar">
            <div className="mp-preview-title"><strong>{C.SEARCH_RESULTS}</strong><span>{resultView === 'changes' ? `${changeRows.length} ${C.CHANGES.toLowerCase()}` : (hasPreviewResults ? t('merchandising.workbench.productsCount', { count: previewTotal }) : '')}</span></div>
            <SegmentedControl
              label={C.PREVIEW_MODE}
              value={resultView}
              onChange={setResultView}
              options={[
                { VALUE: 'preview', LABEL: C.PREVIEW_DRAFT },
                { VALUE: 'original', LABEL: C.ORIGINAL_RESULTS },
                { VALUE: 'changes', LABEL: C.CHANGES_ONLY },
              ]}
            />
            <div className="mp-preview-actions">
              {resultView === 'preview' && hasExclusionActions && (
                <Button
                  className={`mp-excluded-visibility-toggle ${hideExcluded ? 'is-active' : ''}`}
                  variant="outlined"
                  size="small"
                  startIcon={hideExcluded
                    ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                  aria-pressed={hideExcluded}
                  onClick={() => setHideExcluded(value => !value)}
                >
                  {hideExcluded ? C.SHOW_EXCLUDED : C.HIDE_EXCLUDED}
                </Button>
              )}
              <Button size="small" startIcon={<AddIcon />} disabled={readOnly || model.responsePolicy?.mode === 'empty'} onClick={() => { setResultView('preview'); setProductDialogOpen(true); }}>{C.ADD_PRODUCT}</Button>
            </div>
          </div>
          <section className="mp-result-board" aria-busy={previewBusy}>
            {emptyResponsePreview ? <QueryResponsePreview response={model.responsePolicy} /> : resultView === 'changes' ? (
              <QueryChangeReview rows={changeRows} copy={C} locale={intl.locale} />
            ) : (
              <>
                <div className={`mp-product-grid ${draggedProduct ? 'is-sorting' : ''}`}>
                  {products.map((product, index) => {
                    const change = resultView === 'preview' ? model.productChanges[product.ID] : null;
                    const groupState = resultView === 'preview'
                      ? productGroupState(product, effectivePreviewGroupChanges)
                      : { excluded: false, factor: 1 };
                    const groupExcluded = productGroupExclusionApplies(groupState, change);
                    return (
                      <ProductCard
                        key={product.ID}
                        product={product}
                        change={change}
                        displayPosition={change?.POSITION || product.POSITION || ((resultPage - 1) * 20) + index + 1}
                        dragging={draggedProduct === product.ID}
                        dropTarget={dropTarget === product.ID && draggedProduct !== product.ID}
                        groupExcluded={groupExcluded}
                        groupFactor={groupState.factor}
                        interactive={resultView === 'preview'}
                        copy={C}
                        onHide={() => productChange(product.ID, change?.TYPE === 'hidden' ? null : { TYPE: 'hidden' })}
                        onPin={() => pinLast(product.ID)}
                        onUnpin={() => productChange(product.ID, null)}
                        onDragStart={(event, id) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', id); setDraggedProduct(id); }}
                        onDragEnter={setDropTarget}
                        onDragEnd={() => { setDraggedProduct(null); setDropTarget(null); }}
                        onDropAt={dropAt}
                        onMoveEarlier={() => moveProduct(product.ID, -1)}
                        onMoveLater={() => moveProduct(product.ID, 1)}
                      />
                    );
                  })}
                </div>
                {hasPreviewResults && pageCount > 1 && <div className="mp-pagination">
                  <WorkbenchPagination
                    page={resultPage}
                    pageCount={pageCount}
                    onChange={setResultPage}
                    firstLabel={C.FIRST_PAGE}
                    previousLabel={C.PREVIOUS_PAGE}
                    nextLabel={C.NEXT_PAGE}
                    lastLabel={C.LAST_PAGE}
                  />
                </div>}
              </>
            )}
            {previewBusy && <div className="mp-preview-loading" role="status" aria-live="polite"><CircularProgress size={28} thickness={4} /><strong>{C.PREVIEW_REFRESHING}</strong></div>}
          </section></>}
        </main>
        <aside className={`mp-inspector ${inspectorOpen ? 'is-open' : ''}`} aria-label={C.RULE_SETTINGS}>
          <div className="mp-inspector-header"><div><span>{support.cloudEnabled ? t('merchandising.response.inspectorTitle') : C.RULE_SETTINGS}</span>{!support.cloudEnabled && <strong>{actions.length}</strong>}</div><IconButton className="mp-inspector-close" aria-label={C.CLOSE} onClick={() => setInspectorOpen(false)}><CloseIcon /></IconButton></div>
          <div className="mp-inspector-body">
            {extensionEditable && <QueryResponseControl key={`${scopeGeneration}:${sourceId || 'new'}:${responseResetVersion}`}  model={model} baseline={baseline} support={support} disabled={readOnly} selectionPreview={{ busy: previewBusy }} onChange={responsePolicy => setModel(currentModel => ({ ...currentModel, responsePolicy, responsePolicyReset: !responsePolicy && Boolean(baseline.id && baseline.responsePolicy) }))} />}
            {support.cloudEnabled && <h3 className="mp-rule-settings-heading">{C.RULE_SETTINGS}</h3>}
            {model.responsePolicy?.mode === 'selection' && Object.values(model.groupChanges || {}).some(bucket => Object.keys(bucket).length > 0) && <div className="mp-saved-adjustments"><span>{t('merchandising.response.savedAdjustments')}</span><Button size="small" onClick={() => setResultView('changes')}>{t('merchandising.response.reviewChanges')}</Button></div>}
            {!model.responsePolicy && <section className="mp-setting-card mp-rewrite-card">
              <button type="button" className="mp-switch-row" role="switch" aria-checked={rewriteEnabled} onClick={() => {
                if (rewriteEnabled) setModel({ ...model, rewrite: '' });
                setRewriteEnabled(!rewriteEnabled);
              }}>
                <span><strong>{C.SEARCH_INSTEAD}</strong></span><i aria-hidden="true"><b /></i>
              </button>
              {rewriteEnabled && <label className="mp-plain-field"><span>{C.RESULTS_PHRASE}</span><input autoFocus value={model.rewrite} onChange={(event) => setModel({ ...model, rewrite: event.target.value })} /></label>}
            </section>}
            {!support.cloudEnabled && scheduleCard}
            {!model.responsePolicy && ['categories', 'brands'].map((bucket) => (
              <section key={bucket} className="mp-facet-editor">
                <div className="mp-inspector-section-heading"><div><strong>{bucket === 'categories' ? C.CATEGORIES : C.BRANDS}</strong></div></div>
                <div className="mp-facet-list">
                  {(facets[bucket] || []).map(previewFacetItem).map((item) => (
                  <FacetControl
                    key={item.ID}
                    item={item}
                    effect={model.groupChanges[bucket][item.ID]}
                    expanded={expandedFacet === `${bucket}:${item.ID}`}
                    onChange={(value) => setFacet(bucket, item.ID, value)}
                    onToggle={() => setExpandedFacet((currentValue) => currentValue === `${bucket}:${item.ID}` ? null : `${bucket}:${item.ID}`)}
                    copy={C}
                  />
                  ))}

                </div>
              </section>
            ))}
            {support.cloudEnabled && scheduleCard}
          </div>
        </aside>
      </div>
      </fieldset>
      <Dialog className="mp-dialog" open={productDialogOpen} onClose={() => { productSearchGeneration.current += 1; setProductDialogOpen(false); }} maxWidth="sm" fullWidth aria-labelledby="mp-product-search-dialog-title">
        <DialogTitle id="mp-product-search-dialog-title">{C.PRODUCT_SEARCH}</DialogTitle>
        <DialogContent>
          <SearchInput value={productFilter} onChange={setProductFilter} placeholder={C.PRODUCT_SEARCH} />
          <div className="mp-product-picker-list" aria-busy={productSearchBusy}>
            {productOptions.map((product) => (
              <article key={product.id}>
                {(product.image_url || product.thumbnail_url || product.thumbnail || product.image) && <ProductImage product={{ IMAGE: product.image_url || product.thumbnail_url || product.thumbnail || product.image }} size="thumb" />}
                <div><strong>{product.name}</strong><span>{product.reference || `#${product.id}`}</span></div>
                <IconButton className="mp-product-picker-pin" aria-label={`${t('merchandising.workbench.addAndPin')} ${product.name}`} onClick={() => { productSearchGeneration.current += 1; pinLast(String(product.id), true, product); setProductDialogOpen(false); }}><PushPinIcon /></IconButton>
              </article>
            ))}
          </div>
        </DialogContent>
        <DialogActions className="mp-dialog-actions"><Button variant="outlined" onClick={() => { productSearchGeneration.current += 1; setProductDialogOpen(false); }}>{C.CANCEL}</Button></DialogActions>
      </Dialog>
      <UnsavedChangesDialog guard={unsavedGuard} />
    </div>
  );
};

QueryRuleEditor.propTypes = {
  basePath: PropTypes.string.isRequired,
  groupId: PropTypes.string,
  languageId: PropTypes.string.isRequired,
};
QueryRuleEditor.defaultProps = { groupId: null };

export const SearchRulesWorkspace = ({ basePath, editor, groupId }) => {
  const dispatch = useDispatch();
  const enabled = useSelector((state) => state.merchandising.capability.queryGroups);
  const location = useLocation();
  const history = useHistory();
  const routeShopId = useRouteShopId();
  const { adminLanguageId, productProfile } = useShellAppContext();
  const shop = useSelector((state) => (
    state.auth?.authorization?.shopMap?.[String(routeShopId)] || null
  ));
  const shopSettings = useSelector((state) => state.settings || null);
  const isCloud = productProfile?.id === 'cloud';
  const requestedLanguage = new URLSearchParams(location.search).get('language_id');
  const languageId = isCloud
    ? normalizeLanguageScope(requestedLanguage)
    : normalizeLanguageScope(adminLanguageId);
  const languageConfiguration = useMemo(() => ({
    language: shopSettings?.language ?? shop?.language,
    languages: shopSettings?.languages ?? shop?.languages,
  }), [shop, shopSettings?.language, shopSettings?.languages]);
  const languageOptions = useMemo(
    () => cloudLanguageScopes(languageConfiguration, languageId),
    [languageConfiguration, languageId],
  );
  const changeLanguage = useCallback((nextLanguageId) => {
    const params = new URLSearchParams(location.search);
    params.set('language_id', normalizeLanguageScope(nextLanguageId));
    params.delete('rule_id');
    history.push(`${location.pathname}?${params.toString()}`);
  }, [history, location.pathname, location.search]);
  useLayoutEffect(() => {
    dispatch(setMerchandisingQueryLanguage(languageId));
  }, [dispatch, languageId]);
  return (
    <CapabilityNotice enabled={enabled}>
      {editor
        ? (
          <QueryRuleEditor
            key={languageId}
            basePath={basePath}
            groupId={groupId}
            languageId={languageId}
          />
        )
        : (
          <QueryRuleLibrary
            key={languageId}
            basePath={basePath}
            languageId={languageId}
            languageOptions={languageOptions}
            onLanguageChange={changeLanguage}
            showLanguageSelector={isCloud}
          />
        )}
    </CapabilityNotice>
  );
};

SearchRulesWorkspace.propTypes = {
  basePath: PropTypes.string.isRequired,
  editor: PropTypes.bool,
  groupId: PropTypes.string,
};
SearchRulesWorkspace.defaultProps = { editor: false, groupId: null };

export default SearchRulesWorkspace;
